import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import {
  cleanerError,
  normalizeDecimalInput,
  stripTrailingZeros,
} from "@/lib/utils";
import {
  useCreateIngredientPackagingMutation,
  useUpdateIngredientPackagingMutation,
} from "@/redux/services/inventory/ingredientPackaging.api";
import { IngredientPackaging } from "@/redux/services/inventory/inventory.type";
import { toastApiError } from "@/lib/apiErrors";

setLocale(fr);

const POSITIVE_DECIMAL_REGEX = /^\d+(\.\d{1,6})?$/;

export interface IngredientPackagingFormData {
  name: string;
  quantityInBase: string;
  isDefault: boolean;
}

export const ingredientPackagingValidationSchema: yup.ObjectSchema<IngredientPackagingFormData> =
  yup.object().shape({
    name: yup.string().required().label("Nom"),
    quantityInBase: yup
      .string()
      .required()
      .test(
        "is-positive-decimal",
        "La quantité doit être un nombre positif (max 6 décimales)",
        (value) => {
          // "12,5" (French keypad) is read as 12.5, not rejected.
          const normalized = normalizeDecimalInput(value ?? "");
          if (!normalized) return false;
          if (!POSITIVE_DECIMAL_REGEX.test(normalized)) return false;
          return parseFloat(normalized) > 0;
        }
      )
      .label("Quantité"),
    isDefault: yup.boolean().required().default(false),
  });

export const useCrudIngredientPackaging = (
  closeModal: () => void,
  ingredientUuid: string,
  item?: IngredientPackaging
) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setValue,
    watch,
  } = useForm<IngredientPackagingFormData>({
    resolver: yupResolver(ingredientPackagingValidationSchema),
    defaultValues: {
      name: item?.name ?? "",
      quantityInBase: item?.quantityInBase
        ? stripTrailingZeros(item.quantityInBase)
        : "",
      isDefault: item?.isDefault ?? false,
    },
  });

  const [createPackaging, { isLoading: isCreating }] =
    useCreateIngredientPackagingMutation();
  const [updatePackaging, { isLoading: isUpdating }] =
    useUpdateIngredientPackagingMutation();
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    reset({
      name: item?.name ?? "",
      quantityInBase: item?.quantityInBase
        ? stripTrailingZeros(item.quantityInBase)
        : "",
      isDefault: item?.isDefault ?? false,
    });
  }, [item, reset]);

  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  const onSubmit = async (data: IngredientPackagingFormData) => {
    const payload = {
      name: data.name.trim(),
      quantityInBase:
        normalizeDecimalInput(data.quantityInBase) ?? data.quantityInBase.trim(),
      isDefault: data.isDefault,
    };

    const res = item?.id
      ? await updatePackaging({ uuid: item.id, data: payload })
      : await createPackaging({ ingredientUuid, data: payload });

    if ("data" in res) {
      toast.success(
        item?.id ? "Conditionnement modifié" : "Conditionnement ajouté"
      );
      reset();
      closeModal();
    } else if ("error" in res) {
      toastApiError(res.error);
    }
  };

  return {
    register,
    errors,
    setValue,
    watch,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
  };
};
